import type { Rectangle } from './zoneALEcran';

/**
 * Où poser le bandeau du tuto (ticket 33) : en bas, au centre, sans rien couvrir de ce
 * qui compte. Géométrie pure : elle reçoit les rectangles de ce qui est à l'écran, elle rend
 * une position. C'est `BandeauTuto.tsx` qui les lit dans la page.
 *
 * - Une fiche ouverte à droite : le bandeau se centre dans ce qui reste à sa gauche.
 * - Ce qui occupe déjà le bas (la barre « ajouter dans… », l'ampoule) : le bandeau se pose
 *   au-dessus.
 * - La zone à toucher serait dessous : il monte en haut de l'écran.
 */

export interface PlaceDuBandeau {
  left: number;
  largeur: number;
  /** Distance au bas de la fenêtre, ou `null` quand le bandeau est en haut. */
  bas: number | null;
  haut: number | null;
}

const MARGE = 16;
const LARGEUR = 760;
const BAS = 26;
const ECART = 12;

function seCroisent(a: Rectangle, b: Rectangle): boolean {
  return a.x < b.x + b.largeur && b.x < a.x + a.largeur && a.y < b.y + b.hauteur && b.y < a.y + a.hauteur;
}

export function placeDuBandeau(entree: {
  fenetre: { largeur: number; hauteur: number };
  /** La hauteur du bandeau tel qu'il est dessiné. */
  hauteur: number;
  fiche: Rectangle | null;
  /** Ce qui occupe le bas de l'écran. */
  enBas: Rectangle[];
  zone: Rectangle | null;
}): PlaceDuBandeau {
  const { fenetre, hauteur, fiche, enBas, zone } = entree;
  const W = fenetre.largeur;
  const H = fenetre.hauteur;

  // Une fiche qui n'occupe qu'une partie de l'écran (sur un petit écran, elle le prend tout).
  const droite = fiche && fiche.x > W / 3 ? fiche.x - 24 : W;
  const largeur = Math.max(260, Math.min(LARGEUR, droite - MARGE * 2));
  const left = Math.max(MARGE, (droite - largeur) / 2);

  let bas = BAS;
  for (const r of enBas) {
    if (r.x > left + largeur || r.x + r.largeur < left) continue;
    if (r.y < H - bas && r.y + r.hauteur > H - bas - hauteur) bas = Math.max(bas, H - r.y + ECART);
  }
  bas = Math.min(bas, H - hauteur - MARGE);

  const ici: Rectangle = { x: left, y: H - bas - hauteur, largeur, hauteur };
  if (zone && seCroisent(ici, zone)) return { left, largeur, bas: null, haut: MARGE };
  return { left, largeur, bas, haut: null };
}
